import React, { useMemo } from 'react';
import useChartZoom from './useChartZoom';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from '../../lib/charts/highcharts';
import ChartFrame from './ChartFrame';
import { STOCK_CHROME, renderZoom } from './WindTimelineChart';
import { MIN_RANGE_CLOUDBASE_M } from '../../lib/charts/history';
import { LINE_COLORS } from '../../lib/charts/lineColors';
import { zoomingFor } from './zooming';
import { formatStationTime } from '../../lib/charts/time';

/**
 * Temperature (area) + dew point (spline) on the left °C axis, cloud base
 * (dashed) on the right m axis. Grouped because cloud base ≈ (T − Td) × 122:
 * when the two lines converge the ceiling drops. The right axis disappears
 * when the station has no cloud-base data.
 */
const axisTitle = (text, color) => ({
    text, align: 'high', rotation: 0, offset: 0, textAlign: 'left', x: 3, y: -3, style: { color, fontSize: '10px' },
});

export function buildTempSeries(points, labels) {
    const temp = points.map((p) => [p.t, p.temp]);
    const dew = points.map((p) => [p.t, p.dewPoint]);
    const cloud = points.map((p) => [p.t, p.cloudBase]);
    const hasCloud = cloud.some(([, v]) => v !== null);
    const series = [
        {
            name: labels.temperature, type: 'area', yAxis: 0, color: LINE_COLORS.temp, connectNulls: false, threshold: null,
            fillColor: {
                linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                // Theme-aware via CSS variables (softer, lighter hues in dark — see index.scss).
                stops: [[0, 'var(--chart-temp-fill-1)'], [0.35, 'var(--chart-temp-fill-2)'], [0.7, 'var(--chart-temp-fill-3)'], [1, 'var(--chart-temp-fill-4)']],
            },
            data: temp,
        },
        { name: labels.dewPoint, type: 'spline', yAxis: 0, color: LINE_COLORS.dew, lineWidth: 2, connectNulls: false, data: dew },
    ];
    if (hasCloud) {
        series.push({ name: labels.cloudBase, type: 'spline', yAxis: 1, color: LINE_COLORS.cloud, lineWidth: 2, dashStyle: 'Dash', connectNulls: false, data: cloud });
    }
    return { series, hasCloud };
}

const TempDewPointChart = ({ points, labels, zoomLabels, shared, zoomTarget, plotBands, zoomResetKey, height = 300, loading, error, emptyMessage, errorMessage, retryLabel, onRetry }) => {
    // zoomResetKey (range/station) resets to All; new points alone keep the preset and slide it.
    const own = useChartZoom(zoomResetKey ?? points, points);
    // `shared` = { chartRef, xAxisEvents } from useSharedZoom: no own zoom control, no own navigator.
    const chartRef = shared ? shared.chartRef : own.chartRef;
    const xAxisEvents = shared ? shared.xAxisEvents : own.xAxisEvents;
    const options = useMemo(() => {
        const { series, hasCloud } = buildTempSeries(points, labels);
        const yAxis = [{
            title: axisTitle('°C', 'var(--chart-temp)'), opposite: false,
            tickColor: 'var(--chart-temp)', lineColor: 'var(--chart-temp)', tickWidth: 1, tickLength: 6, lineWidth: 1,
            labels: { x: -3, style: { color: 'var(--chart-temp)', fontSize: '10px' } }, startOnTick: false, endOnTick: false, tickAmount: 5,
        }];
        if (hasCloud) {
            yAxis.push({
                title: axisTitle('m', 'var(--chart-cloud)'), gridLineWidth: 0, opposite: true,
                tickColor: 'var(--chart-cloud)', lineColor: 'var(--chart-cloud)', tickWidth: 1, tickLength: 6, lineWidth: 1,
                startOnTick: false, endOnTick: false, minRange: MIN_RANGE_CLOUDBASE_M, tickAmount: 5,
                labels: { x: 3, y: -3, style: { color: 'var(--chart-cloud)', fontSize: '10px' } },
            });
        }
        return {
            chart: { height, backgroundColor: 'transparent', panning: { enabled: true, type: 'x' }, zooming: zoomingFor(shared) },
            ...STOCK_CHROME,
            navigator: { enabled: !shared, height: 36 },
        navigator: { enabled: !shared, height: 36 },
            title: { text: undefined },
            xAxis: { type: 'datetime', crosshair: true, ordinal: false, events: xAxisEvents, plotBands, dateTimeLabelFormats: { hour: '%H:%M', day: '%a %H:%M', week: '%d %b', month: '%d %b' } },
            yAxis,
            tooltip: {
                shared: true, useHTML: true, borderRadius: 6, padding: 8,
                formatter() {
                    const lines = [`<span style="font-size:10px;color:var(--chart-text-subtle)">${formatStationTime(Number(this.x), 'DD.MM.YYYY HH:mm')}</span>`];
                    for (const p of this.points || []) {
                        const isCloud = p.series.name === labels.cloudBase;
                        const v = p.y === null || p.y === undefined ? '—' : isCloud ? String(Math.round(p.y)) : p.y.toFixed(1);
                        lines.push(`<span style="color:${p.color}">●</span> ${p.series.name}: <b>${v}${isCloud ? ' m' : ' °C'}</b>`);
                    }
                    return lines.join('<br>');
                },
            },
            plotOptions: { series: { animation: { duration: 600 }, marker: { enabled: false, states: { hover: { enabled: true } } }, dataGrouping: { enabled: false } } },
            legend: { enabled: true },
            accessibility: { description: 'Temperature, dew point and cloud base over time' },
            series,
        };
    }, [points, labels, height, xAxisEvents, shared, plotBands]);

    return (
        <div>
            {renderZoom(shared, own, zoomLabels, zoomTarget)}
            <ChartFrame ariaLabel={labels.temperature} height={height} loading={loading} error={error}
                        empty={!points || points.length < 2} emptyMessage={emptyMessage}
                        errorMessage={errorMessage} retryLabel={retryLabel} onRetry={onRetry}>
                <HighchartsReact ref={chartRef} highcharts={Highcharts} constructorType="stockChart" options={options} />
            </ChartFrame>
        </div>
    );
};

export default TempDewPointChart;
